import { useMemo, useState } from "react";
import { Search, ArrowUpRight, Eye, Store, Building2, Globe, FlaskConical, Landmark, LayoutGrid, Sparkles } from "lucide-react";
import { Reveal, SectionHead, GoldBtn, PageHero } from "@/components/Shared";
import Accordion from "@/components/Accordion";
import EnquiryForm from "@/components/EnquiryForm";
import { PAVILIONS, POD_CAPABILITIES, EXHIBITORS, FLOOR_ZONES } from "@/data/content";

const WHY = [
    { icon: Eye, title: "Unmatched Visibility", text: "10,000+ expected footfall across three days — founders, investors, corporates and media on one floor." },
    { icon: Store, title: "A Ready Stage", text: "Plug-and-play startup pods and curated pavilions — arrive, plug in, and the bazaar does the rest." },
    { icon: Building2, title: "Business, Engineered", text: "B2B connect, corporate co-creation and investor speed meets built into the exhibition design." },
    { icon: Globe, title: "National & Global", text: "15+ international delegations and country pavilions along the Global Startup Street." },
];

const FLOOR_ICONS = [Store, Building2, Globe, FlaskConical, Landmark, LayoutGrid, Sparkles];

const TARIFF = [
    { space: "Startup Pod", detail: "Compact plug-and-play pod inside the Innovation Bazaar", price: "Coming Soon" },
    { space: "Sectoral Pavilion Booth", detail: "Branded booth within a thematic pavilion", price: "Coming Soon" },
    { space: "Corporate Pavilion", detail: "Large-format presence for enterprises and industry", price: "Coming Soon" },
    { space: "Country Pavilion", detail: "National showcase on the Global Startup Street", price: "Coming Soon" },
    { space: "National Showcase", detail: "Mission-driven institutional presence", price: "Coming Soon" },
    { space: "State Pavilion", detail: "Dedicated pavilion for each state to showcase its flagship startups", price: "Coming Soon" },
    { space: "Incubator / CoE Pavilion", detail: "Showcase space for startup incubation centres and Centres of Excellence", price: "Coming Soon" },
    { space: "Special Zone Presence", detail: "Presence inside one of the five special zones", price: "Coming Soon" },
];

const EXHIBITION_FAQS = [
    { q: "Who can exhibit at Startup MahaBazaar 2027?", a: "Startups, enterprises, R&D institutions, country delegations and ecosystem organisations can exhibit through startup pods, sectoral pavilions and special zones." },
    { q: "What is a Startup Pod?", a: "A compact plug-and-play exhibition unit — your startup story, QR-based investor connect, live demo slots and digital discovery, all built in." },
    { q: "What are the exhibition tariffs?", a: "Exhibition tariffs are being finalised and will be announced soon. Submit an exhibitor enquiry and our team will share the tariff deck with you first." },
    { q: "How do I book exhibition space?", a: "Submit the exhibitor enquiry form on this page. Our exhibition team will reach out with layouts, availability and booking steps." },
    { q: "Can international organisations exhibit?", a: "Yes — country pavilions and the Global Startup Street are designed for international participation and cross-border collaboration." },
];

const Directory = () => {
    const [q, setQ] = useState("");
    const [sector, setSector] = useState("All");
    const [stage, setStage] = useState("All");
    const [expanded, setExpanded] = useState(null);

    const sectors = useMemo(() => ["All", ...new Set(EXHIBITORS.map((e) => e.category))], []);
    const stages = useMemo(() => ["All", ...new Set(EXHIBITORS.map((e) => e.stage))], []);

    const results = useMemo(
        () =>
            EXHIBITORS.filter(
                (e) =>
                    (sector === "All" || e.category === sector) &&
                    (stage === "All" || e.stage === stage) &&
                    (!q.trim() || e.name.toLowerCase().includes(q.trim().toLowerCase()) || e.category.toLowerCase().includes(q.trim().toLowerCase())),
            ),
        [q, sector, stage],
    );

    return (
        <div data-testid="exhibitor-directory">
            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative">
                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--gold)]" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exhibitors…"
                        className="field !w-60 !pl-10 !py-2.5 text-sm" data-testid="directory-search" />
                </div>
                <select value={sector} onChange={(e) => setSector(e.target.value)} className="field !w-52 !py-2.5 text-sm" data-testid="directory-sector-filter" aria-label="Filter by sector">
                    {sectors.map((s) => <option key={s} value={s}>{s === "All" ? "All Sectors" : s}</option>)}
                </select>
                <select value={stage} onChange={(e) => setStage(e.target.value)} className="field !w-48 !py-2.5 text-sm" data-testid="directory-stage-filter" aria-label="Filter by stage">
                    {stages.map((s) => <option key={s} value={s}>{s === "All" ? "All Stages" : s}</option>)}
                </select>
                <span className="text-[0.6rem] tracking-[0.28em] uppercase text-muted-luxe ml-auto">{results.length} Exhibitors</span>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.map((e, i) => (
                    <div key={e.id} className="card-luxe p-6" data-testid={`exhibitor-card-${i}`}>
                        <div className="card-glow" />
                        <div className="flex items-center justify-between">
                            <span className="text-[0.55rem] tracking-[0.3em] uppercase text-[var(--gold)]">{e.category}</span>
                            <span className="text-[0.55rem] tracking-[0.2em] uppercase text-muted-luxe">{e.id}</span>
                        </div>
                        <h3 className="font-display text-xl mt-3 text-[var(--ivory)]">{e.name}</h3>
                        <p className="mt-2 text-xs text-muted-luxe leading-relaxed">{e.desc}</p>
                        <div className="mt-4 flex items-center gap-4 text-[0.58rem] tracking-[0.2em] uppercase text-muted-luxe">
                            <span>{e.pavilion.split(" ")[0]} Pavilion</span>
                            <span className="text-[var(--champagne)]">Booth {e.booth}</span>
                        </div>
                        <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} data-testid={`exhibitor-explore-${i}`}
                            className="mt-5 inline-flex items-center gap-2 text-[0.6rem] tracking-[0.3em] uppercase text-[var(--gold)] hover:gap-3.5 transition-all">
                            Explore <ArrowUpRight size={11} />
                        </button>
                        <div className={`grid transition-all duration-500 ${expanded === e.id ? "[grid-template-rows:1fr] opacity-100" : "[grid-template-rows:0fr] opacity-0"}`}>
                            <div className="overflow-hidden">
                                <div className="pt-4 mt-4 border-t hairline grid grid-cols-3 gap-3 text-[0.58rem] tracking-[0.16em] uppercase">
                                    <div><p className="text-muted-luxe">Stage</p><p className="text-[var(--ivory)] mt-1">{e.stage}</p></div>
                                    <div><p className="text-muted-luxe">Technology</p><p className="text-[var(--ivory)] mt-1">{e.tech}</p></div>
                                    <div><p className="text-muted-luxe">Location</p><p className="text-[var(--ivory)] mt-1">{e.location}</p></div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {results.length === 0 && <p className="py-14 text-center text-sm text-muted-luxe border hairline mt-8">No exhibitors match these filters.</p>}
            <p className="mt-6 text-[0.6rem] tracking-[0.24em] uppercase text-muted-luxe/70">Preview directory — full exhibitor profiles to be announced.</p>
        </div>
    );
};

export default function Exhibition() {
    return (
        <div data-testid="exhibition-page">
            <PageHero
                testid="exhibition-hero"
                eyebrow="The Exhibition"
                title={<>The Grand Floor of <span className="gold-text">Indian Innovation</span></>}
                sub="120+ startups, 10+ thematic pavilions, country showcases and special zones — exhibit at the bazaar where Bharat's future is on display."
            >
                <GoldBtn to="#book" testid="exhibition-book-btn">Book Your Space</GoldBtn>
                <GoldBtn to="/venue#map" variant="outline" testid="exhibition-map-btn">View Event Map</GoldBtn>
            </PageHero>

            <section className="py-24 md:py-32">
                <div className="container-x">
                    <SectionHead eyebrow="Why Exhibit" title={<>Put Your Venture on the <span className="gold-text">Grand Stage</span></>} />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {WHY.map((w, i) => (
                            <Reveal key={w.title} delay={i * 70}>
                                <div className="card-luxe tilt p-7 h-full">
                                    <div className="card-glow" />
                                    <w.icon size={22} strokeWidth={1.2} className="text-[var(--gold)]" />
                                    <h3 className="font-display text-xl mt-6 text-[var(--ivory)]">{w.title}</h3>
                                    <p className="mt-3 text-xs text-muted-luxe leading-relaxed">{w.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60">
                <div className="container-x">
                    <SectionHead eyebrow="The Exhibition Experience" title={<>Seven Worlds on <span className="gold-text">One Floor</span></>}
                        sub="An exhibition architecture inspired by India's grandest technology summits — organised for discovery." />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {FLOOR_ZONES.map((z, i) => {
                            const Icon = FLOOR_ICONS[i];
                            return (
                                <Reveal key={z.name} delay={(i % 4) * 60} className={i === 0 || i === 5 ? "lg:col-span-2" : ""}>
                                    <div className="card-luxe tilt p-7 h-full" data-testid={`floor-zone-${i}`}>
                                        <div className="card-glow" />
                                        <Icon size={20} strokeWidth={1.2} className="text-[var(--gold)]" />
                                        <h3 className="font-display text-xl mt-5 text-[var(--ivory)]">{z.name}</h3>
                                        <p className="mt-2.5 text-xs text-muted-luxe leading-relaxed">{z.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="container-x">
                    <SectionHead eyebrow="The Pavilions" title={<>10+ Thematic <span className="gold-text">Experiences</span></>} />
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {PAVILIONS.map((p, i) => (
                            <Reveal key={p.n} delay={(i % 5) * 60}>
                                <div className="border hairline p-5 h-full hover:border-[var(--line)] transition-colors group">
                                    <div className="flex items-center justify-between">
                                        <p.icon size={18} strokeWidth={1.2} style={{ color: p.tint }} />
                                        <span className="font-display text-[var(--ivory)]/25 group-hover:text-[var(--gold)] transition-colors">{p.n}</span>
                                    </div>
                                    <h3 className="font-display text-base mt-4 leading-snug text-[var(--ivory)]">{p.name}</h3>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-10 text-center">
                        <GoldBtn to="/experience#pavilions" variant="outline" testid="exhibition-pavilions-btn">Explore All Pavilions</GoldBtn>
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60 noise relative overflow-hidden">
                <div className="container-x">
                    <SectionHead eyebrow="Startup Pods" title={<>Plug In. <span className="gold-text">Showcase. Get Discovered.</span></>} />
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {POD_CAPABILITIES.map((c, i) => (
                            <Reveal key={c.n} delay={i * 70}>
                                <div className="card-luxe p-6 h-full">
                                    <div className="card-glow" />
                                    <span className="font-display gold-text text-2xl">{c.n}</span>
                                    <h3 className="font-display text-lg mt-4 text-[var(--ivory)]">{c.title}</h3>
                                    <p className="mt-2.5 text-xs text-muted-luxe leading-relaxed">{c.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="container-x">
                    <SectionHead eyebrow="Exhibitor Profile" title={<>The Exhibitor <span className="gold-text">Directory</span></>}
                        sub="Search and filter the startups and exhibitors of MahaBazaar 2027 by sector, pavilion and stage." />
                    <Reveal className="mt-14"><Directory /></Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60">
                <div className="container-x">
                    <SectionHead eyebrow="Exhibition Tariff" title={<>Spaces for Every <span className="gold-text">Ambition</span></>}
                        sub="Tariffs are being finalised with our exhibition partners and will be announced soon." />
                    <Reveal className="mt-14 max-w-4xl mx-auto">
                        <div className="card-luxe" data-testid="tariff-table">
                            <div className="overflow-x-auto">
                            <table className="w-full text-left min-w-[540px]">
                                <thead>
                                    <tr className="border-b hairline">
                                        <th className="px-7 py-5 text-[0.6rem] tracking-[0.3em] uppercase text-[var(--gold)] font-medium">Space</th>
                                        <th className="px-7 py-5 text-[0.6rem] tracking-[0.3em] uppercase text-[var(--gold)] font-medium">Includes</th>
                                        <th className="px-7 py-5 text-[0.6rem] tracking-[0.3em] uppercase text-[var(--gold)] font-medium text-right">Tariff</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[rgba(201,162,75,0.08)]">
                                    {TARIFF.map((t) => (
                                        <tr key={t.space} className="hover:bg-[rgba(201,162,75,0.04)] transition-colors">
                                            <td className="px-7 py-5 font-display text-lg text-[var(--ivory)]">{t.space}</td>
                                            <td className="px-7 py-5 text-sm text-muted-luxe">{t.detail}</td>
                                            <td className="px-7 py-5 text-right text-[0.62rem] tracking-[0.26em] uppercase text-[var(--champagne)]">{t.price}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32" id="book">
                <div className="container-x grid lg:grid-cols-[1fr_1.2fr] gap-14 items-start">
                    <Reveal>
                        <span className="eyebrow no-lines">Book Your Space</span>
                        <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                            Claim Your Place on the <span className="gold-text">Bazaar Floor</span>
                        </h2>
                        <p className="mt-6 text-muted-luxe text-base leading-relaxed">
                            Submit an exhibitor enquiry and our team will share pavilion layouts, availability and
                            the tariff deck as soon as bookings open.
                        </p>
                        <div className="mt-10">
                            <Accordion items={EXHIBITION_FAQS} testidPrefix="exhibition-faq" />
                        </div>
                    </Reveal>
                    <Reveal delay={150}>
                        <EnquiryForm defaultCategory="Exhibition Enquiry" heading="Exhibitor Enquiry" sub="Spaces, pods and pavilions — tell us what you want to build." testidPrefix="exhibitor-enquiry" />
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
